import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/urlConfig';
import { apiFetch } from '../config/api';
import { GoogleMark } from '../components/AuthLayout';
import Logo from '../components/Logo';
import FotoDaEntrada from '../components/entrada/FotoDaEntrada';
import SeletorDeIdioma from '../components/SeletorDeIdioma';
import { POSTS, tituloCurto } from '../config/posts';
import { Link, useIdioma } from '../i18n';
import '../styles/NewHome.css';
import '../styles/NewLogin.css';

/**
 * Entrada na conta, no desenho da página inicial nova. Convive com
 * `pages/Login.js` sem tocar nele: qual das duas atende `/login` é o import em
 * `App.js`, e voltar é trocar aquela linha.
 *
 * A sessão continua a mesma do Login antigo: cookie httpOnly, sem token e sem
 * localStorage. O que muda é o formulário. E-mail e senha existem desde o
 * início, para o gerenciador de senhas preencher os dois. O erro vem por status
 * e diz o próximo passo, em vez de repassar o texto da API, porque o 429 do
 * limitador chega sempre em inglês.
 */

/* A API responde 401 igual para conta inexistente, conta do Google e senha errada. */
const ERRO_POR_STATUS = { 401: 'entrada.erroDados', 429: 'entrada.erroMuitas' };

const ultimoPost = [...POSTS].sort((a, b) => new Date(b.data) - new Date(a.data))[0];

const NewLogin = ({ setUser }) => {
  const { t, idioma } = useIdioma();
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const senhaRef = useRef(null);
  const [busca] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /* { chave, campo }: campo é 'email', 'senha', 'ambos' ou null (falha que não é do que foi digitado).
     O login pelo Google que falha volta do backend como /login?error=auth_failed. */
  const [erro, setErro] = useState(() =>
    busca.get('error') === 'auth_failed' ? { chave: 'entrada.erroGoogle', campo: null } : null
  );
  const [verSenha, setVerSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [indoAoGoogle, setIndoAoGoogle] = useState(false);

  const falhar = (chave, campo) => {
    setErro({ chave, campo });
    if (campo === 'email') emailRef.current.focus();
    if (campo === 'senha' || campo === 'ambos') {
      senhaRef.current.focus();
      senhaRef.current.select();
    }
  };

  const entrar = async (e) => {
    e.preventDefault();
    setErro(null);
    if (!emailRef.current.validity.valid) return falhar('entrada.faltaEmail', 'email');
    if (!password) return falhar('entrada.faltaSenha', 'senha');

    setEnviando(true);
    try {
      const resposta = await apiFetch('/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!resposta.ok) {
        const chave = ERRO_POR_STATUS[resposta.status] || 'entrada.erroRede';
        return falhar(chave, resposta.status === 401 ? 'ambos' : null);
      }
      const data = await resposta.json();
      setUser({
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        avatar: data.user.avatar
      });
      window.dataLayer.push({ event: 'login', method: 'email' });
      navigate('/user-home');
    } catch {
      falhar('entrada.erroRede', null);
    } finally {
      setEnviando(false);
    }
  };

  const invalido = (campo) => (erro && (erro.campo === campo || erro.campo === 'ambos')) || undefined;
  const descrito = erro ? 'entrada-erro' : undefined;

  return (
    <div className="nh nh-grade entrada">
      <FotoDaEntrada>
        {ultimoPost && (
          <Link to={`/blog/${ultimoPost.slug}`} className="entrada-novo">
            {t('entrada.novoNoBlog', { titulo: tituloCurto(ultimoPost, idioma) })}
            <span className="nh-seta" aria-hidden="true">→</span>
          </Link>
        )}
      </FotoDaEntrada>

      <main className="entrada-principal">
        <div className="entrada-caixa">
          {/* Só no telefone, onde o painel da foto (e a marca dentro dele) sai. */}
          <header className="entrada-topo">
            <Link to="/">
              <Logo size={28} />
            </Link>
          </header>

          <h1>{t('entrada.titulo')}</h1>
          <p className="entrada-texto">{t('entrada.texto')}</p>

          <a
            href={`${API_URL}/auth/google/login`}
            className="entrada-google"
            aria-busy={indoAoGoogle || undefined}
            onClick={() => setIndoAoGoogle(true)}
          >
            <GoogleMark />
            {t(indoAoGoogle ? 'entrada.abrindoGoogle' : 'entrada.comGoogle')}
          </a>
          <p className="entrada-ou">{t('entrada.ouEmail')}</p>

          <form className="entrada-form" onSubmit={entrar} noValidate>
            <div className="entrada-campo">
              <label htmlFor="entrada-email">{t('entrada.email')}</label>
              <input
                id="entrada-email"
                ref={emailRef}
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={invalido('email')}
                aria-describedby={descrito}
              />
            </div>

            <div className="entrada-campo">
              <div className="entrada-rotulo">
                <label htmlFor="entrada-senha">{t('entrada.senha')}</label>
                <Link to="/forgot-password">{t('entrada.esqueceu')}</Link>
              </div>
              <div className="entrada-senha">
                <input
                  id="entrada-senha"
                  ref={senhaRef}
                  name="password"
                  type={verSenha ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={invalido('senha')}
                  aria-describedby={descrito}
                />
                <button type="button" aria-controls="entrada-senha" onClick={() => setVerSenha((v) => !v)}>
                  {t(verSenha ? 'entrada.esconder' : 'entrada.mostrar')}
                </button>
              </div>
            </div>

            {erro && (
              <p className="entrada-erro" id="entrada-erro" role="alert">
                {t(erro.chave)}
              </p>
            )}

            <button
              type="submit"
              className="sp-btn sp-btn-on-section nh-btn entrada-entrar"
              disabled={enviando}
            >
              {t(enviando ? 'entrada.entrando' : 'entrada.botao')}
            </button>
          </form>

          <p className="entrada-conta">
            {t('entrada.semConta')} <Link to="/signup">{t('entrada.criarConta')}</Link>
          </p>
          <p className="entrada-legal">
            <Link to="/privacy-policy">{t('site.privacidade')}</Link>
            <Link to="/terms-of-service">{t('site.termos')}</Link>
          </p>
          <SeletorDeIdioma />
        </div>
      </main>
    </div>
  );
};

export default NewLogin;
